import { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ScanFace, Upload, RotateCcw, Search, ShieldCheck, Sparkles, Camera } from "lucide-react";
import api, { apiErr, mediaUrl } from "@/lib/api";import { Loader } from "@/components/common/Loader";
import { PhotoMasonry } from "@/components/gallery/PhotoMasonry";
import { Lightbox } from "@/components/gallery/Lightbox";
import { ClientNav, SubmitBar } from "@/components/client/ClientChrome";
import { SubmitConfirm, SubmitSuccess } from "@/components/client/SubmitFlow";
import { LeadModal } from "@/components/client/LeadModal";
import { useClientGallery } from "@/lib/useClientGallery";

export default function FindMyPhotos() {
  const { eventId } = useParams();
  const g = useClientGallery(eventId);
  const fileRef = useRef(null);
  const [consent, setConsent] = useState(false);
  const [selfie, setSelfie] = useState(null); // {file|url, preview}
  const [phase, setPhase] = useState("idle"); // idle | searching | results
  const [results, setResults] = useState([]);
  const [idx, setIdx] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [success, setSuccess] = useState(0);
  const [leadPhoto, setLeadPhoto] = useState(null);

  if (g.loading) return <div className="min-h-screen bg-cream"><Loader full label="Preparing face search" /></div>;
  if (g.error || !g.event) return <div className="min-h-screen flex items-center justify-center text-ink/60 bg-cream">Gallery not found.</div>;

  const pickFile = (file) => { if (!file) return; setSelfie({ file, preview: URL.createObjectURL(file) }); };

  const useDemoFace = async (url) => {
    const res = await fetch(mediaUrl(url));
    const blob = await res.blob();
    const file = new File([blob], "selfie.jpg", { type: blob.type || "image/jpeg" });
    setSelfie({ file, preview: url });
  };

  const runSearch = async () => {
    if (!selfie) return;
    setPhase("searching");
    const fd = new FormData();
    fd.append("selfie", selfie.file);
    try {
      const { data } = await api.post(`/public/events/${eventId}/search`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // merge selection/favorite state from gallery
      const map = Object.fromEntries(g.photos.map((p) => [p.id, p]));
      setResults(data.photos.map((p) => ({ ...p, selected: map[p.id]?.selected || false, favorite: map[p.id]?.favorite || false })));
      setPhase("results");
      if (data.match_count === 0) toast("No matches found. Try a clearer, front-facing selfie.");
    } catch (e) { toast.error(apiErr(e)); setPhase("idle"); }
  };

  const toggleSelect = async (photo) => {
    const val = !photo.selected;
    setResults((prev) => prev.map((p) => (p.id === photo.id ? { ...p, selected: val } : p)));
    await api.post(`/public/events/${eventId}/photos/${photo.id}/select`, { selected: val }).catch(() => {});
  };
  const toggleFavorite = async (photo) => {
    const val = !photo.favorite;
    setResults((prev) => prev.map((p) => (p.id === photo.id ? { ...p, favorite: val } : p)));
    await api.post(`/public/events/${eventId}/photos/${photo.id}/favorite`, { favorite: val }).catch(() => {});
  };
  const selectedCount = results.filter((p) => p.selected).length;
  const doSubmit = async (comments) => {
    try { const r = await g.submit(comments); setConfirm(false); setSuccess(r.count); } catch { toast.error("Could not submit."); }
  };

  return (
    <div className="min-h-screen bg-cream pb-28">
      <ClientNav eventId={eventId} eventName={g.event.name} />

      {phase !== "results" && (
        <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-16 h-16 rounded-2xl bg-gold/15 flex items-center justify-center mx-auto mb-6"><ScanFace className="w-8 h-8 text-gold" /></div>
            <h1 className="font-serif text-4xl sm:text-5xl text-ink font-light">Find every moment<br />you were part of.</h1>
            <p className="text-ink/60 mt-4">Upload a clear selfie and we'll find photographs containing a similar face.</p>
          </motion.div>

          {phase === "searching" ? (
            <div className="mt-14"><Loader label="Finding your moments" /></div>
          ) : (
            <>
              <div className="mt-10">
                {selfie ? (
                  <div className="relative w-40 h-40 mx-auto rounded-2xl overflow-hidden border-2 border-gold gold-glow-sm">
                    <img src={selfie.preview} alt="selfie" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div onClick={() => consent && fileRef.current?.click()} data-testid="selfie-dropzone"
                    className={`w-full rounded-2xl border-2 border-dashed p-10 transition-colors ${consent ? "border-border hover:border-gold/40 cursor-pointer" : "border-border/50 opacity-50 cursor-not-allowed"}`}>
                    <Upload className="w-8 h-8 text-gold mx-auto mb-3" />
                    <div className="text-ink text-sm">Tap to upload a selfie</div>
                    <div className="text-xs text-ink/60 mt-1">or use your camera on mobile</div>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden" data-testid="selfie-input" onChange={(e) => pickFile(e.target.files?.[0])} />
              </div>

              <DemoFaces eventId={eventId} onPick={useDemoFace} disabled={!consent} />

              <label className="flex items-start gap-3 mt-8 text-left bg-white border border-maroon/10 rounded-xl p-4 cursor-pointer">
                <input type="checkbox" data-testid="consent-checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-[#d4af37] w-4 h-4" />
                <span className="text-sm text-ink/70"><ShieldCheck className="w-4 h-4 text-gold inline mr-1" />I consent to PMR Events processing my selfie to find my photos. My face data is encrypted, never shared, and can be deleted anytime.</span>
              </label>

              <div className="flex items-center justify-center gap-3 mt-6">
                {selfie && <button data-testid="retake-selfie" onClick={() => { setSelfie(null); fileRef.current && (fileRef.current.value = ""); }} className="inline-flex items-center gap-2 bg-white border border-maroon/10 rounded-full px-5 py-3 text-ink text-sm hover:border-gold/40"><RotateCcw className="w-4 h-4" /> Retake</button>}
                <button data-testid="search-btn" onClick={runSearch} disabled={!consent || !selfie}
                  className="inline-flex items-center gap-2 bg-gold text-black font-medium rounded-full px-7 py-3 hover:bg-gold-light transition-colors disabled:opacity-40">
                  <Search className="w-4 h-4" /> Find My Photos
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {phase === "results" && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 text-gold text-sm"><Sparkles className="w-4 h-4" /> Photos of You</div>
              <h1 className="font-serif text-4xl text-ink font-light mt-1">{results.length} moments found</h1>
              <p className="text-ink/60 text-sm mt-1">Ranked by face-match confidence · tap to select for your album</p>
            </div>
            <button data-testid="new-search" onClick={() => { setPhase("idle"); setSelfie(null); setResults([]); }} className="inline-flex items-center gap-2 bg-white border border-maroon/10 rounded-full px-5 py-2.5 text-ink text-sm hover:border-gold/40 self-start"><Camera className="w-4 h-4" /> New search</button>
          </div>
          {results.length === 0 ? (
            <div className="text-center py-20 text-ink/60">No matching photos found. Try a clearer, well-lit selfie facing the camera.</div>
          ) : (
            <PhotoMasonry photos={results} onOpen={setIdx} selectable favoritable watermark={g.event.watermark} onToggleSelect={toggleSelect} onToggleFavorite={toggleFavorite} />
          )}
          <Lightbox photos={results} index={idx} onClose={() => setIdx(null)} setIndex={setIdx} selectable favoritable downloadable={g.event.download_permission} watermark={g.event.watermark} onToggleSelect={toggleSelect} onToggleFavorite={toggleFavorite} onDownload={setLeadPhoto} />
        </div>
      )}

      <LeadModal photo={leadPhoto} eventId={eventId} onClose={() => setLeadPhoto(null)} />

      <SubmitBar count={selectedCount} onSubmit={() => setConfirm(true)} />
      <SubmitConfirm open={confirm} count={selectedCount} submitting={g.submitting} onCancel={() => setConfirm(false)} onConfirm={doSubmit} />
      <SubmitSuccess open={success > 0} count={success} onClose={() => setSuccess(0)} />
    </div>
  );
}

function DemoFaces({ eventId, onPick, disabled }) {
  const [faces, setFaces] = useState(null);
  if (faces === null) {
    api.get(`/public/events/${eventId}`).then((r) => setFaces(r.data.event.demo_faces || [])).catch(() => setFaces([]));
    return null;
  }
  if (!faces.length) return null;
  return (
    <div className="mt-6">
      <div className="text-xs text-ink/60 mb-2">No selfie handy? Try a sample guest face:</div>
      <div className="flex items-center justify-center gap-3">
        {faces.map((f, i) => (
          <button key={i} data-testid={`demo-face-${i}`} disabled={disabled} onClick={() => onPick(f.url)}
            className="group relative w-16 h-16 rounded-full overflow-hidden border-2 border-border hover:border-gold disabled:opacity-40 transition-colors">
            <img src={mediaUrl(f.url)} alt={f.label} className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}
